<template>
    <div class="z-pagination" v-if="data.totalPages > 1">
        <div class="z-pagination__wrapper">
            <z-button
                v-if="showMore && !(data.totalPages === data.currentPage)"
                class="z-pagination__button"
                kind="bordered"
                @click="changePage(nextPage, 'load-more')"
                :loading="loading"
            >
                <span>{{ showMore.text ? showMore.text : 'Загрузить еще' }}</span>
                <template v-slot:right>
                    <z-icon 
                        width="36" 
                        height="36" 
                        name="arrows/round-arrow-big" 
                        dir="down"
                    />
                </template>
            </z-button>
            <nav aria-label="pagination navigation" class="z-pagination__nav" v-if="showNav">
                <ul class="z-pagination__list">
                    <li
                        v-if="showPrev"
                        class="z-pagination__list-nav"
                    >
                        <button
                            type="button"
                            aria-label="Предыдущая страница"
                            :class="[
                                'z-pagination__item',
                                'z-pagination__item-prev',
                                'z-pagination__item-icon',
                                { 'is-disabled' : data.currentPage === 1 }
                            ]"
                            @click="changePage(prevPage)"
                            key="prev"
                        >
                            <z-icon
                                class="z-pagination__icon z-pagination__icon--prev"
                                :name="prevIcon"
                                width="20"
                                height="20"
                                dir="left"
                            />
                        </button>
                    </li>
                    <template v-if="hasFirst">
                        <li class="z-pagination__list-item">
                            <button
                                type="button"
                                aria-label="страница 1"
                                class="z-pagination__item z-pagination__item-background"
                                @click="changePage(1)"
                                key="first"
                            >
                                <span class="z-pagination__number z-pagination__number--single">
                                    1
                                </span>
                            </button>
                        </li>
                        <li class="z-pagination__list-item">
                            <span
                                aria-label="devider"
                                class="z-pagination__divider"
                                v-if="isDividerShown('start')"
                            >
                                <z-icon :name="dotsIcon" width="24" height="24"/>
                            </span>
                        </li>
                    </template>
                    <li
                        v-for="(item, index) in nav.pages"
                        :key="`${item}-${index}`"
                        :class="[
                            'z-pagination__list-item', 
                            {'is-current': item === data.currentPage}
                        ]"
                    >
                        <button
                            type="button"
                            :class="[
                                'z-pagination__item',
                                'z-pagination__item-background',
                                { 'is-active' : item === data.currentPage }
                            ]"
                            @click="changePage(item)"
                        >
                            <span
                                :aria-label="`страница ${item}`"
                                :class="['z-pagination__number', getNumberClass(item)]"
                            >
                                {{ item }}
                            </span>
                        </button>
                    </li>
                    <template v-if="hasLast">
                        <li v-if="isDividerShown('end')" class="z-pagination__list-item">
                            <span
                                aria-label="devider"
                                class="z-pagination__divider"
                            >
                                <z-icon :name="dotsIcon" width="24" height="24"/>
                            </span>
                        </li>
                        <li class="z-pagination__list-item">
                            <button
                                type="button"
                                class="z-pagination__item z-pagination__item-background"
                                @click="changePage(data.totalPages)"
                                key="last"
                            >
                                <span
                                    :class="['z-pagination__number', getNumberClass(data.totalPages)]"
                                >
                                    {{ data.totalPages }}
                                </span>
                            </button>
                        </li>
                    </template>
                    <li
                        v-if="showNext"
                        class="z-pagination__list-nav"
                    >
                        <button
                            type="button"
                            aria-label="Следующая страница"
                            :class="[
                                'z-pagination__item',
                                'z-pagination__item-next',
                                'z-pagination__item-icon',
                                { 'is-disabled' : data.currentPage === data.totalPages }
                            ]"
                            @click="changePage(nextPage)"
                            key="next"
                        >
                            <z-icon
                                class="z-pagination__icon"
                                :name="nextIcon"
                                width="20"
                                height="20"
                                dir="right"
                            />
                        </button>
                    </li>
                </ul>
            </nav>
        </div>
    </div>
</template>

<script setup>
import {computed} from 'vue'
import {debounce} from 'throttle-debounce'

const props = defineProps({
    data: Object,
    showNav: {
        type: Boolean,
        default: true
    },
    showPrev: {
        type: Boolean,
        default: true
    },
    showNext: {
        type: Boolean,
        default: true
    },
    showMore: {
        type: Boolean,
        default: true
    },
    loading: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['change-page'])

const dotsIcon = 'general/multipoint'
const prevIcon = 'arrows/caret-light'
const nextIcon = 'arrows/caret-light'

const nav = computed(() => {
    let pages = []
    let start = (props.data.currentPage - props.data.pageRange) > 0 ? props.data.currentPage - props.data.pageRange : 1
    let end = (props.data.currentPage + props.data.pageRange) <= props.data.totalPages ? props.data.currentPage + props.data.pageRange : props.data.totalPages

    if (start === 1 && end + 1 <= props.data.totalPages && props.data.currentPage === 1) {
        end += 1
    }

    if (end === props.data.totalPages && start - 1 >= 1 && props.data.currentPage !== props.data.totalPages - 1) {
        start -= 1
    }

    for (let i = start; i <= end; i++) {
        pages.push(i)
    }

    return {
        start: start,
        end: end,
        pages: pages
    }
})

const prevPage = computed(() => {
    return props.data.currentPage - 1
})

const nextPage = computed(() => {
    return props.data.currentPage + 1
})

const hasFirst = computed(() => {
    if (!nav.value.start) return false
    return nav.value.start !== 1
})

const hasLast = computed(() => {
    if (!nav.value.end) return false
    return nav.value.end !== props.data.totalPages
})

const getNumberClass = (item) => {
    let number = 'z-pagination__number--single'

    if (String(item).length === 2) {
        number = 'z-pagination__number--double'
    }

    if (String(item).length >= 3) {
        number = 'z-pagination__number--triple'
    }

    return number
}

const changePage = debounce(0, function (page, type = 'change-page') {
    if (page > 0 && page <= props.data.totalPages) {
        emit('change-page', {page: page, type: type})
    }
})

const isDividerShown = (type) => {
    let result = false
    if (type === 'start') {
        result = (nav.value.pages[0] - 1) !== 1
    }
    if (type === 'end') {
        result = (nav.value.pages[nav.value.pages.length - 1] + 1) !== props.data.totalPages
    }

    return result
}

defineExpose({
    props
})
</script>

<style lang="scss" src="./index.scss"></style>
